import type {
  RouteRecordRaw,
  NavigationGuard,
  RouteRecordRedirectOption,
  RouteLocationNormalized,
} from 'vue-router';
import Permission from '@/modules/current-user/permissions/permissions';
import useNewOrgStore from '@/modules/create-org/store';
import { FeatureFlag } from '@/composables/feature-flags';
import { encodeSearchQueryParams } from '@@/packages/shared/libs/search/useQueryParams';
import { searchQueryParamsLatestScanResultFindingsForAsset } from '@/modules/external-monitoring/composables';
import { ANSWER_SET_ROUTE_NAME } from '@/modules/offline-assessment/routeNames';
import {
  defaultChildWithFlag,
  redirectWhenFlagged,
  requireAnyFlag,
  requireFlag,
} from './guards/flag';
import { requireAnswerSetOfSupplier } from './guards/answerSet';

// The answering view's views take the set as `answerSetId`, the prop spelling of
// the path's `answerSetID`.
function answerSetRouteProps(route: RouteLocationNormalized) {
  return {
    answerSetId: route.params.answerSetID,
    domainLetter: route.params.domainLetter,
  };
}

function requireOrganisationStep(step: number): NavigationGuard {
  return (_to, _from, next) => {
    const newOrgStore = useNewOrgStore();
    if (newOrgStore.unlockedStep < step) {
      next({ name: 'suppliers-new' });
    } else {
      next();
    }
  };
}

// Shared by the current `findings/asset/:assetName?` route and its legacy path:
// both turn an asset name into an encoded findings filter and drop the param.
const findingsOfAssetRedirect: RouteRecordRedirectOption = (to) => ({
  name: 'suppliers-profile-public-data/findings',
  query: encodeSearchQueryParams(
    searchQueryParamsLatestScanResultFindingsForAsset(
      Array.isArray(to.params.assetName) ? to.params.assetName[0] : to.params.assetName,
    ),
  ),
  params: { ...to.params, assetName: undefined },
  replace: true,
});

export default [
  {
    path: 'suppliers/new',
    name: 'suppliers-new',
    component: () => import('@/modules/create-org/SupplierNew.vue'),
    meta: {
      permissions: [Permission.ClientsSuppliersManage],
    },
    redirect: { name: 'suppliers-new-organisation' },
    children: [
      {
        path: 'org',
        name: 'suppliers-new-organisation',
        component: () => import('@/modules/create-org/Organisation.vue'),
      },
      {
        path: 'contact',
        name: 'suppliers-new-contact',
        component: () => import('@/modules/create-org/Contact.vue'),
        beforeEnter: [requireOrganisationStep(2)],
      },
    ],
  },
  {
    // Legacy redirect - supplier profile and public profile have been merged
    path: 'suppliers/:id/public',
    name: 'suppliers-public-profile',
    redirect: { name: 'suppliers-profile' },
  },
  {
    path: 'suppliers/:id',
    name: 'suppliers-profile',
    component: () => import('@/modules/profile/SupplierProfile.vue'),
    props: true,
    redirect: { name: 'suppliers-profile-overview' },
    meta: {
      permissions: [Permission.ClientsSuppliersView],
    },
    children: [
      {
        name: 'suppliers-profile-overview',
        path: 'overview',
        component: () => import('@/modules/profile/components/overview/OverviewTab.vue'),
      },
      {
        name: 'suppliers-profile-activity',
        path: 'activity',
        component: () => import('@/modules/activity-feed/ActivityFeedConnection.vue'),
        meta: { connectionCapability: 'connectionAllowsActivity' },
      },
      {
        // The Assessment tab links here, so it shows as active on every child.
        // Carries no component, so each child renders its own views.
        name: 'suppliers-profile-assessment-root',
        path: 'assessment',
        redirect: { name: 'suppliers-profile-assessment' },
        children: [
          {
            name: 'suppliers-profile-assessment',
            path: ':domainLetter?',
            components: {
              default: () =>
                import('@/modules/profile/components/assessment/AssessmentTabRouter.vue'),
              hero: () => import('@/modules/profile/components/assessment/AssessmentHero.vue'),
              sidebar: () =>
                import('@/modules/profile/components/assessment/AssessmentSidebar.vue'),
              filters: () =>
                import('@/modules/profile/components/assessment/AssessmentFilters.vue'),
            },
            meta: {
              dataTable: true,
              filtersAboveSidebar: true,
            },
            // For some reason it complains about the hero type above without the type coercion
          } as RouteRecordRaw,
          {
            name: ANSWER_SET_ROUTE_NAME,
            path: 'answer-sets/:answerSetID/:domainLetter?',
            beforeEnter: [
              requireFlag(FeatureFlag.ClientManagedAssessment),
              requireAnswerSetOfSupplier,
            ],
            components: {
              default: () => import('@/modules/offline-assessment/AnswerSetDomain.vue'),
              hero: () => import('@/modules/offline-assessment/AnswerSetHero.vue'),
              sidebar: () => import('@/modules/offline-assessment/AnswerSetSidebar.vue'),
            },
            props: {
              default: answerSetRouteProps,
              hero: answerSetRouteProps,
              sidebar: answerSetRouteProps,
            },
          } as RouteRecordRaw,
        ],
      },
      {
        path: 'public-data',
        beforeEnter: [
          requireAnyFlag(
            FeatureFlag.ViewExternalIntelligence,
            FeatureFlag.ExternalMonitoringPaidClient,
          ),
        ],
        components: {
          default: () => import('@@/packages/shared/components/RouterPassThrough.vue'),
          sidebar: () => import('@/modules/public-data/PublicDataSidebar.vue'),
          // Routes each sub-page's own hero into the page layout's hero slot.
          hero: () => import('@/modules/public-data/PublicDataHeroRouter.vue'),
        },
        props: true,
        children: [
          {
            // The tab links here rather than at the section itself, so that
            // arriving from a sibling still resolves to a sub-page. Carries no
            // component: the guard always redirects before anything renders.
            name: 'suppliers-profile-public-data',
            path: '',
            beforeEnter: [
              defaultChildWithFlag(
                'suppliers-profile-public-data',
                FeatureFlag.ViewExternalIntelligence,
                {
                  to: 'suppliers-profile-public-data/trust-centre',
                  defaultTo: 'suppliers-profile-public-data/scans',
                },
              ),
            ],
          },
          {
            name: 'suppliers-profile-public-data/trust-centre',
            path: 'trust-centre',
            beforeEnter: [requireFlag(FeatureFlag.ViewExternalIntelligence)],
            components: {
              default: () => import('@/modules/public-data/TrustCentre.vue'),
              hero: () => import('@/modules/public-data/TrustCentreHero.vue'),
            },
          },
          {
            name: 'suppliers-profile-public-data/policies',
            path: 'policies',
            beforeEnter: [requireFlag(FeatureFlag.ViewExternalIntelligence)],
            components: {
              default: () => import('@/modules/public-data/Policies.vue'),
              hero: () => import('@/modules/public-data/PoliciesHero.vue'),
            },
          },
          {
            name: 'suppliers-profile-public-data/certificates',
            path: 'certificates',
            beforeEnter: [requireFlag(FeatureFlag.ViewExternalIntelligence)],
            components: {
              default: () => import('@/modules/certificates/CertificatesPage.vue'),
              hero: () => import('@/modules/public-data/CertificatesHero.vue'),
            },
            props: { default: { layout: 'table' } },
            meta: {
              dataTable: true,
            },
          },
          {
            name: 'suppliers-profile-public-data/scans',
            path: 'scans',
            beforeEnter: [requireFlag(FeatureFlag.ExternalMonitoringPaidClient)],
            components: {
              default: () => import('@/modules/external-monitoring/SupplierProfileScans.vue'),
              hero: () => import('@/modules/external-monitoring/SupplierProfileScansHero.vue'),
            },
          },
          {
            name: 'suppliers-profile-public-data/assets',
            path: 'assets',
            beforeEnter: [requireFlag(FeatureFlag.ExternalMonitoringPaidClient)],
            components: {
              default: () => import('@/modules/external-monitoring/SupplierProfileAssets.vue'),
              hero: () => import('@/modules/external-monitoring/SupplierProfileAssetsHero.vue'),
            },
          },
          {
            name: 'suppliers-profile-public-data/findings',
            path: 'findings',
            beforeEnter: [requireFlag(FeatureFlag.ExternalMonitoringPaidClient)],
            components: {
              default: () => import('@/modules/external-monitoring/SupplierProfileFindings.vue'),
              hero: () => import('@/modules/external-monitoring/SupplierProfileFindingsHero.vue'),
            },
            props: { default: true },
          },
          {
            name: 'suppliers-profile-public-data/findings-of-asset',
            path: 'findings/asset/:assetName?',
            redirect: findingsOfAssetRedirect,
          },
        ],
        // For some reason it complains about the hero type above without the type coercion
      } as RouteRecordRaw,
      {
        // Legacy redirects — external monitoring moved into the Public data
        // tab. Keep until old links and bookmarks have aged out.
        path: 'external-monitoring',
        children: [
          { path: '', redirect: { name: 'suppliers-profile-public-data' } },
          { path: 'scans', redirect: { name: 'suppliers-profile-public-data/scans' } },
          { path: 'assets', redirect: { name: 'suppliers-profile-public-data/assets' } },
          { path: 'findings', redirect: { name: 'suppliers-profile-public-data/findings' } },
          {
            path: 'findings/asset/:assetName?',
            redirect: findingsOfAssetRedirect,
          },
        ],
      },
      {
        name: 'suppliers-profile-threats',
        path: 'threats/:threatId?',
        component: () => import('@/modules/emerging-threats-common/ThreatsProfileTab.vue'),
        meta: {
          permissions: [Permission.ClientsEmergingThreatsView],
        },
        props: true,
      },
      {
        name: 'suppliers-profile-risks',
        path: 'risks/:riskNumber?',
        component: () => import('@/modules/risks/RisksProfileTab.vue'),
        props: true,
      },
      {
        name: 'suppliers-profile-evidence',
        path: 'evidence',
        components: {
          default: () => import('@/modules/profile/components/evidence/EvidenceTabRouter.vue'),
          sidebar: () => import('@/modules/profile/components/evidence/EvidenceSidebar.vue'),
          // Routes each sub-page's own hero into the page layout's hero slot.
          hero: () => import('@/modules/profile/components/evidence/EvidenceHeroRouter.vue'),
        },
        props: true,
        redirect: { name: 'suppliers-profile-evidence/uploaded-documents' },
        children: [
          {
            name: 'suppliers-profile-evidence/uploaded-documents',
            path: '',
            components: {
              default: () => import('@/modules/profile/components/evidence/UploadedDocuments.vue'),
              hero: () => import('@/modules/profile/components/evidence/UploadedDocumentsHero.vue'),
            },
            props: { default: true },
            // Drops the container padding, so the table runs full width.
            meta: {
              dataTable: true,
            },
          },
          {
            name: 'suppliers-profile-evidence/certificates',
            path: 'certificates',
            // Discovered certificates live under Public data with external
            // intelligence. Kept so old links and bookmarks still land there.
            beforeEnter: [
              redirectWhenFlagged(
                FeatureFlag.ViewExternalIntelligence,
                'suppliers-profile-public-data/certificates',
              ),
            ],
            components: {
              default: () => import('@/modules/certificates/CertificatesPage.vue'),
              hero: () =>
                import('@/modules/profile/components/evidence/EvidenceCertificatesHero.vue'),
            },
            props: { default: true },
          },
          {
            name: 'suppliers-profile-evidence/your-uploads',
            path: 'your-uploads',
            components: {
              default: () => import('@/modules/client-documents/ClientDocuments.vue'),
              hero: () => import('@/modules/client-documents/ClientDocumentsHero.vue'),
            },
            props: { default: true },
            // Drops the container padding, so the table runs full width.
            meta: {
              dataTable: true,
            },
          },
        ],
        // Same as the assessment record above: without the coercion the extra
        // `hero` view widens the sibling records' inferred `components` type.
      } as RouteRecordRaw,
      {
        name: 'suppliers-profile-discussions',
        path: 'discussions/:discussionID?',
        component: () => import('@/modules/discussions/DiscussionsTabView.vue'),
        props: (route) => ({
          discussionId: route.params.discussionID,
          query: route.query,
        }),
        // The tab scrolls its list and thread itself, like the inbox, so it needs a
        // bounded body: in a page that scrolls, the thread grows to its full height.
        meta: {
          mainContainerScrollable: true,
          connectionCapability: 'connectionAllowsDiscussions',
        },
      },
      {
        name: 'suppliers-profile-notes',
        path: 'notes',
        component: () => import('@/modules/client-notes/ClientNotes.vue'),
        meta: {
          permissions: [Permission.ClientsSuppliersNotesView],
        },
      },
      {
        name: 'suppliers-profile-about',
        path: 'about',
        components: {
          default: () => import('@@/packages/shared/components/RouterPassThrough.vue'),
          sidebar: () => import('@/modules/org-info/OrgInfoSidebar.vue'),
        },
        redirect: { name: 'suppliers-profile-about/basic-info' },
        children: [
          {
            name: 'suppliers-profile-about/basic-info',
            path: 'info',
            props: true,
            component: () => import('@/modules/org-info/OrgInfoBasic.vue'),
          },
          {
            name: 'suppliers-profile-about/products',
            path: 'products',
            props: true,
            component: () => import('@/modules/org-info/OrgInfoProducts.vue'),
          },
          {
            name: 'suppliers-profile-about/critical-suppliers',
            path: 'critical-suppliers',
            props: true,
            component: () => import('@/modules/org-info/OrgInfoCriticalSuppliers.vue'),
            meta: { connectionCapability: 'connectionAllowsCriticalSuppliers' },
          },
        ],
      },
    ],
  },
  {
    path: 'policies',
    children: [
      {
        path: '',
        name: 'policies',
        component: () => import('@/modules/policy/PolicyListPage.vue'),
      },
      {
        path: 'matrix',
        name: 'policy-matrix',
        component: () => import('@/modules/policy/PolicyMatrix.vue'),
      },
      {
        path: ':id',
        name: 'policies-add-edit',
        meta: {
          permissions: [Permission.ClientsPoliciesManage],
        },
        component: () => import('@/modules/policy/components/policy-page/PolicyPage.vue'),
        props: true,
      },
    ],
  },
] satisfies RouteRecordRaw[];
